import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';

/**
 * driversList  — array of driver objects (already sorted externally)
 * busyMap      — Map<driverId, { status: 'pending'|'in_transit', shipmentNumber? }>
 * value        — selected driverId string
 * onChange     — (driverId) => void
 * disabled     — bool
 * placeholder  — string
 * required     — bool
 */
const DriverSelect = ({
  driversList = [],
  busyMap = new Map(),
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select driver',
  required = false,
}) => {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const containerRef        = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDriverStatus = (driver) => {
    if (driver.status === 'inactive') return 'inactive';
    const busy = busyMap.get(driver.id);
    if (busy) return busy.shipmentStatus; // 'pending' | 'in_transit'
    return 'available';
  };

  const sortScore = (driver) => {
    const s = getDriverStatus(driver);
    if (s === 'available')   return 0;
    if (s === 'pending')     return 1;
    if (s === 'in_transit')  return 2;
    return 3; // inactive
  };

  const sorted = [...driversList].sort((a, b) => sortScore(a) - sortScore(b));

  const filtered = sorted.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.driverCode?.toLowerCase().includes(search.toLowerCase()) ||
    d.phone?.includes(search)
  );

  const selectedDriver = driversList.find(d => d.id === value);

  const statusBadge = (driver) => {
    const s = getDriverStatus(driver);
    if (s === 'available') return null;
    if (s === 'inactive') {
      return (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 border border-gray-300">
          Inactive
        </span>
      );
    }
    const label = s === 'in_transit' ? 'In Transit' : 'Pending';
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-300">
        <ExclamationCircleIcon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const selectedStatus = selectedDriver ? getDriverStatus(selectedDriver) : null;

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        disabled={disabled}
        className={`input-field w-full flex items-center justify-between text-left ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {selectedDriver ? (
            <>
              <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate text-sm font-medium text-gray-800">
                {selectedDriver.name}
                {selectedDriver.driverCode && (
                  <span className="text-gray-400 font-normal ml-1">({selectedDriver.driverCode})</span>
                )}
              </span>
              {selectedStatus && selectedStatus !== 'available' && (
                <span className="flex-shrink-0">{statusBadge(selectedDriver)}</span>
              )}
            </>
          ) : (
            <span className="text-gray-400 text-sm">
              {disabled ? 'Loading drivers…' : placeholder}
            </span>
          )}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search drivers…"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          {/* List */}
          <ul className="max-h-60 overflow-y-auto py-1">
            {!required && (
              <li
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
              >
                — No driver —
              </li>
            )}

            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-gray-400 text-center">No drivers match your search</li>
            )}

            {filtered.map(driver => {
              const s     = getDriverStatus(driver);
              const isBusy = s === 'in_transit' || s === 'pending';
              const isInactive = s === 'inactive';
              const isSelected = driver.id === value;

              return (
                <li
                  key={driver.id}
                  onClick={() => {
                    onChange(driver.id);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`
                    flex items-center justify-between px-3 py-2 cursor-pointer
                    ${isSelected  ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-gray-50'}
                    ${(isBusy || isInactive) ? 'opacity-80' : ''}
                  `}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <UserIcon className={`w-4 h-4 flex-shrink-0 ${isBusy ? 'text-red-400' : isInactive ? 'text-gray-300' : 'text-gray-400'}`} />
                    <div className="min-w-0">
                      <div className={`text-sm font-medium truncate ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>
                        {driver.name}
                        {driver.driverCode && (
                          <span className="text-gray-400 font-normal ml-1 text-xs">({driver.driverCode})</span>
                        )}
                      </div>
                      {driver.phone && (
                        <div className="text-xs text-gray-400 truncate">{driver.phone}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    {isBusy && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-300">
                        <ExclamationCircleIcon className="w-3 h-3" />
                        {s === 'in_transit' ? 'In Transit' : 'Busy'}
                      </span>
                    )}
                    {isInactive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 border border-gray-300">
                        Inactive
                      </span>
                    )}
                    {!isBusy && !isInactive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-600 border border-green-300">
                        Available
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DriverSelect;
